



















































































































































































































import { Component, Vue } from 'nuxt-property-decorator';
import { MetaInfo } from 'vue-meta';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import {
  getLeadText,
  getFeatureTitles,
  getFeatureTexts,
  getFeatureImages,
  getFilterCategories,
  getRandomFilterCategory,
  getSectionTitles,
  getAllHotels,
  getKvImages,
  getHotelsImages,
  FEATURE_COUNT,
  NEW_HOTEL_SLUGS,
  BRAND_SLOGAN,
  BRAND_DESCRIPTION,
  AvailableLanguage,
  FilterCategory,
} from './-data';
import HotelImage from './-HotelImage.vue';
import { HSelect, HPortalItem, HHotelToBooking } from '~/components';
import { HotelBrandDetail } from '~/schemes';
import { generateHreflang } from '~/helpers';

/**
 * NOTE: (this as any) について
 * Vue 2 + nuxt-property-decorator の制約により、@Component 内の
 * data(), computed, methods は this から型認識されないため使用。
 * gf へのリプレース時は Composition API (<script setup>) で型安全に書き直す。
 *
 * NOTE: GSAP について
 * gf へのリプレース時は GSAP を使わず、CSS アニメーション + Intersection Observer で実装する。
 */
@Component({
  components: { HSelect, HotelImage, HPortalItem, HHotelToBooking },
  layout: 'default',
  async asyncData({ $hotel }) {
    // CMSから界ブランドのデータを取得
    const brand = await $hotel.brandDetailBySlug('kai');
    if (brand) {
      $hotel.setCurrentBrand(brand);
    }
    return { brand };
  },
  head() {
    const vm = this as any;
    const lang = vm.$language.current;
    const brand: HotelBrandDetail | null = vm.brand;

    // ブランドヘッダーロゴ表示用の属性
    const htmlAttrs: MetaInfo['htmlAttrs'] = {};
    if (vm.brandHeaderIsActive) {
      htmlAttrs['data-brand-header-active'] = '1';
    }

    // hreflang設定
    const link: MetaInfo['link'] = brand ? generateHreflang(vm, { brand }) : [];

    // Googleフォント
    link.push({
      rel: 'stylesheet',
      href:
        'https://fonts.googleapis.com/css2?family=Shippori+Mincho:wght@400;500;600&family=Zen+Kaku+Gothic+New:wght@400;500&display=swap',
    });

    if (!brand) {
      return { title: '界', link, script: [], htmlAttrs };
    }

    const { siteName, description, image } = brand.meta;

    // OGP・meta設定
    const meta: MetaInfo['meta'] = [
      { hid: 'og:site_name', property: 'og:site_name', content: siteName },
      { hid: 'og:title', property: 'og:title', content: siteName },
      {
        hid: 'og:url',
        property: 'og:url',
        content: `${vm.$navigation.origin}/${lang}/brands/kai/`,
      },
    ];

    if (description) {
      meta.push(
        { hid: 'description', name: 'description', content: description },
        {
          hid: 'og:description',
          property: 'og:description',
          content: description,
        },
      );
    }

    if (image) {
      meta.push({
        hid: 'og:image',
        property: 'og:image',
        content: vm.$res.img(image),
      });
    }

    // ファビコン
    if (brand.favicon) {
      link.push({
        hid: 'favicon',
        rel: 'icon',
        type: 'image/x-icon',
        href: vm.$res.img(brand.favicon),
      });
    }

    // 構造化データ
    const url = `${vm.$navigation.origin}/${lang}/brands/kai/`;
    const companyName = vm.$i18n.t('name.hr') as string;
    const companyUrl = vm.$i18n.t('url.globalTop') as string;
    const slogan = BRAND_SLOGAN[lang as AvailableLanguage];
    const structuredDescription = BRAND_DESCRIPTION[lang as AvailableLanguage];

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Hotel',
      name: siteName,
      logo:
        brand.logo && brand.logo.horizontal && brand.logo.horizontal.light
          ? vm.$res.img(brand.logo.horizontal.light)
          : undefined,
      url,
      parentOrganization: {
        '@type': 'Organization',
        name: companyName,
        url: companyUrl,
      },
      image: image ? vm.$res.img(image) : undefined,
      description: structuredDescription,
      brand: {
        '@type': 'Brand',
        name: siteName,
        url,
        slogan,
      },
    };

    const script = [
      {
        hid: 'structured-data-brand',
        type: 'application/ld+json',
        json: structuredData,
      },
    ];

    return {
      titleTemplate: (titleChunk: string) => {
        if (!titleChunk) {
          return siteName;
        }
        return `${titleChunk} | ${siteName}`;
      },
      htmlAttrs,
      meta,
      link,
      script,
    };
  },
  data() {
    return {
      brand: null as HotelBrandDetail | null,
      featureCount: FEATURE_COUNT,
      selectedFilter: 'scenery' as FilterCategory, // SSR/クライアント一致のため固定値
      hotelsSwiper: null as Swiper | null,
      newHotelsSwiper: null as Swiper | null,
      isNarrow: false,
      isMounted: false,
      isReady: false, // 初期表示フェードイン用
    };
  },
  computed: {
    /** ブランドヘッダーロゴの表示切り替え用 */
    logoIsScrollOuted(): boolean {
      const vm = this as any;
      if (!vm.isMounted) return false;
      return vm.$window.scrollTop > 250;
    },
    brandHeaderIsActive(): boolean {
      const vm = this as any;
      return vm.brand && vm.brand.useHeaderLogo ? vm.logoIsScrollOuted : false;
    },
    lang(): AvailableLanguage {
      return (this as any).$language.current;
    },
    kvImages() {
      return getKvImages((this as any).lang);
    },
    hotelsImages() {
      return getHotelsImages((this as any).lang);
    },
    sectionTitles() {
      return getSectionTitles((this as any).lang);
    },
    featureTitles(): string[] {
      return getFeatureTitles((this as any).lang);
    },
    featureTexts(): { title: string; sub: string }[] {
      return getFeatureTexts((this as any).lang);
    },
    featureImages(): string[] {
      return getFeatureImages((this as any).lang);
    },
    filterCategories() {
      return getFilterCategories((this as any).lang);
    },
    filterItems() {
      return (this as any).filterCategories.map((c: any) => ({
        label: c.label,
        value: c.value,
      }));
    },
    leadTexts(): string[] {
      return getLeadText((this as any).lang, (this as any).isNarrow);
    },
    hotels(): { slug: string; name: string; location: string }[] {
      const allHotels = getAllHotels((this as any).lang);
      const filter = (this as any).selectedFilter;
      const category = (this as any).filterCategories.find(
        (c: any) => c.value === filter,
      );
      if (!category) {
        return [];
      }
      return allHotels.filter((hotel) => category.hotels.includes(hotel.slug));
    },
    newHotels(): { slug: string; name: string; location: string }[] {
      const allHotels = getAllHotels((this as any).lang);
      return allHotels.filter((hotel) => NEW_HOTEL_SLUGS.includes(hotel.slug));
    },
  },
  methods: {
    /** ご当地につかろう用 Swiper を初期化 */
    initHotelsSwiper() {
      const vm = this as any;
      const hotelsEl = vm.$refs.hotelsSwiper;
      if (!hotelsEl) return;

      // 既存のSwiperがあれば破棄
      if (vm.hotelsSwiper) {
        vm.hotelsSwiper.destroy(true, true);
      }

      const isWide = window.innerWidth >= 768; // CSSのmq(wide)と合わせる
      const spaceBetween = isWide
        ? Math.min(Math.max(window.innerWidth * 0.092, 110), 177)
        : 30;

      vm.hotelsSwiper = new Swiper(hotelsEl, {
        autoplay: {
          delay: 4000,
          disableOnInteraction: false,
        },
        slidesPerView: isWide ? 'auto' : 1.4,
        spaceBetween,
        centeredSlides: true,
        loop: true,
        speed: 800,
        navigation: {
          prevEl: '.my-kai-new__hotels__nav--prev',
          nextEl: '.my-kai-new__hotels__nav--next',
        },
      });
    },
    initSwipers() {
      // ご当地につかろう用 Swiper
      (this as any).initHotelsSwiper();

      // 新施設のご紹介用 Swiper
      const newHotelsEl = (this as any).$refs.newHotelsSwiper;
      if (newHotelsEl) {
        const isWide = window.innerWidth >= 768;
        const newHotelsSwiper = new Swiper(newHotelsEl, {
          slidesPerView: 'auto',
          spaceBetween: isWide ? 60 : 20,
          centeredSlides: true,
          initialSlide: isWide ? 1 : 0, // SP: 最初から、PC: 2つめから
          loop: true, // 無限ループ
          speed: 600,
          navigation: {
            prevEl: '.my-kai-new__new-hotels__nav--prev',
            nextEl: '.my-kai-new__new-hotels__nav--next',
          },
        });
        (this as any).newHotelsSwiper = newHotelsSwiper;

        // 手動でまるぽちを作成（実スライド数分）
        const paginationEl = document.querySelector(
          '.my-kai-new__new-hotels__pagination',
        );
        if (paginationEl) {
          const totalSlides = NEW_HOTEL_SLUGS.length; // 実際のスライド数
          const initialActive = isWide ? 1 : 0;
          paginationEl.innerHTML = '';
          for (let i = 0; i < totalSlides; i++) {
            const bullet = document.createElement('span');
            bullet.className =
              'swiper-pagination-bullet' +
              (i === initialActive ? ' swiper-pagination-bullet-active' : '');
            bullet.addEventListener('click', () => {
              newHotelsSwiper.slideToLoop(i); // ループ対応
            });
            paginationEl.appendChild(bullet);
          }
          // スライド変更時にアクティブを更新
          newHotelsSwiper.on('slideChange', () => {
            const bullets = paginationEl.querySelectorAll(
              '.swiper-pagination-bullet',
            );
            bullets.forEach((b, idx) => {
              b.classList.toggle(
                'swiper-pagination-bullet-active',
                idx === newHotelsSwiper.realIndex, // ループ対応
              );
            });
          });
        }
      }
    },
    onFilterChange() {
      // TODO(gf): 画像読み込み完了後にフェードイン表示する
      // フィルター変更時にhotelsSwiper のみ再初期化
      (this as any).$nextTick(() => {
        (this as any).initHotelsSwiper();
      });
    },
  },
  mounted() {
    if (process.client) {
      // ブランドヘッダーロゴ表示用
      (this as any).isMounted = true;

      // ランダムなカテゴリーを選択（SSRハイドレーション後）
      (this as any).selectedFilter = getRandomFilterCategory();

      // デバイス判定
      (this as any).isNarrow = window.innerWidth < 768;

      // フェードイン表示
      // TODO(gf): document.fonts.ready でフォント読み込み完了を待つ
      (this as any).$nextTick(() => {
        (this as any).isReady = true;
      });

      gsap.registerPlugin(ScrollTrigger);

      // === 設定値 ===
      const headerHeight = window.innerWidth >= 768 ? 72 : 50;
      const vh = (percent: number) => window.innerHeight * (percent / 100); // vh単位をpxに変換

      // フェード完了までのスクロール距離
      const greenFadeScroll = vh(50); // 50vhスクロールでgreen完了
      const yugeFadeScroll = vh(80); // 80vhスクロールでyuge完了

      // === Lead セクションを pin ===
      const pinScrollDistance = vh(150);
      ScrollTrigger.create({
        trigger: '.my-kai-new__lead',
        start: `top ${headerHeight}px`,
        end: `+=${pinScrollDistance}px`,
        pin: true,
        anticipatePin: 1,
      });

      // === Green フェードイン ===
      gsap.fromTo(
        '.my-kai-new__lead__green',
        { opacity: 0 },
        {
          opacity: 1,
          scrollTrigger: {
            trigger: '.my-kai-new__lead',
            start: `top ${headerHeight}px`,
            end: `+=${greenFadeScroll}px`,
            scrub: true,
            onLeave: (self) => {
              self.kill();
              gsap.set('.my-kai-new__lead__green', { opacity: 1 });
            },
          },
        },
      );

      // === Yuge フェードイン ===
      gsap.fromTo(
        '.my-kai-new__lead__yuge',
        { opacity: 0 },
        {
          opacity: 0.6,
          scrollTrigger: {
            trigger: '.my-kai-new__lead',
            start: `top ${headerHeight}px`,
            end: `+=${yugeFadeScroll}px`,
            scrub: true,
            onLeave: (self) => {
              self.kill();
              gsap.set('.my-kai-new__lead__yuge', { opacity: 0.6 });
              // yuge完了 → リード文フェードイン
              gsap.to('.my-kai-new__lead__text', {
                opacity: 1,
                duration: 1,
                ease: 'power2.out',
              });
            },
          },
        },
      );

      // === Feature セクション ===
      const featureImages = document.querySelectorAll(
        '.my-kai-new__feature__img',
      );
      const featureTitles = document.querySelectorAll(
        '.my-kai-new__feature__title',
      );
      const featureTexts = document.querySelectorAll(
        '.my-kai-new__feature__text',
      );
      const snapPoints = document.querySelectorAll(
        '.my-kai-new__feature__snap',
      );

      // 各スナップポイントで切り替え
      snapPoints.forEach((snap, index) => {
        if (index === 0) return; // 最初はスキップ

        ScrollTrigger.create({
          trigger: snap,
          start: 'top center',
          onEnter: () => {
            // 次のセットをフェードイン（まとめてアニメーション）
            gsap.to(
              [featureImages[index], featureTitles[index], featureTexts[index]],
              { opacity: 1, duration: 0.8 },
            );
            // 前のセットをフェードアウト（まとめてアニメーション）
            gsap.to(
              [
                featureImages[index - 1],
                featureTitles[index - 1],
                featureTexts[index - 1],
              ],
              { opacity: 0, duration: 0.8 },
            );
          },
          onLeaveBack: () => {
            // 戻る時は前のをフェードイン（まとめてアニメーション）
            gsap.to(
              [
                featureImages[index - 1],
                featureTitles[index - 1],
                featureTexts[index - 1],
              ],
              { opacity: 1, duration: 0.8 },
            );
            // 現在のをフェードアウト（まとめてアニメーション）
            gsap.to(
              [featureImages[index], featureTitles[index], featureTexts[index]],
              { opacity: 0, duration: 0.8 },
            );
          },
        });
      });

      // === Swiper 初期化 ===
      // selectedFilter変更後のDOM更新を待ってから初期化
      (this as any).$nextTick(() => {
        (this as any).initSwipers();
      });
    }
  },
  beforeDestroy() {
    if (process.client) {
      ScrollTrigger.killAll();
      const vm = this as any;
      if (vm.hotelsSwiper) {
        vm.hotelsSwiper.destroy(true, true);
      }
      if (vm.newHotelsSwiper) {
        vm.newHotelsSwiper.destroy(true, true);
      }
      // ブランドヘッダーをクリア（他ページへの影響を防ぐ）
      vm.$hotel.setCurrentBrand(null);
    }
  },
})
export default class MyKaiNewPage extends Vue {}
